import { useMemo, useCallback, ReactElement } from "react"
import { useParams } from "react-router-dom"
import _ from "lodash"
import { SxProps, Tab, useTheme } from "@mui/material"
import { deepmerge } from "@mui/utils"
import SavedSearchRoundedIcon from "@mui/icons-material/SavedSearchRounded"
import { singular as singularize } from "pluralize"

import { IFeatureCategoryObj } from "hooks/feature/useFeatureQuickSearch/types"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { ScrollableTab } from "components/ScrollableTab"

export interface IFeatureToggleChipTabBarProps {
  categories: IFeatureCategoryObj[]
  onClick: (type: string, category: string) => void
  sx?: SxProps
}

export const FeatureToggleChipTabBar: React.FC<
  IFeatureToggleChipTabBarProps
> = ({ categories = [], onClick = () => {}, sx = {} }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mapTheme } = useVenue()

  const { root, tab, fallbackTab } =
    theme.components[`FeatureToggleChipTabBar`]?.styleOverrides || {}
  const { featureType, category } = useParams()
  const activeCategory =
    featureType && category
      ? `${singularize(`${featureType}`)}::${category}`
      : "none"

  const getFeatureToggleButtonProps = useCallback(
    (featureCategory) => {
      const { type, category } = featureCategory
      const iconPath = _.get(
        mapTheme,
        `${type}.${category}.label.marker.symbol.markerPath`
      )
      const image = _.get(
        mapTheme,
        `${category}.${category}.label.marker.symbol.markerFile`
      )
      return iconPath
        ? { type, category, icon_path: iconPath }
        : { type, category, image }
    },
    [mapTheme]
  )

  const amenityButtonProps = useMemo(
    () => categories.map((amenity) => getFeatureToggleButtonProps(amenity)),
    [categories, getFeatureToggleButtonProps]
  )

  return (
    <ScrollableTab
      color="primary"
      value={activeCategory}
      className="FeatureToggleChipTabBar-root"
      sx={deepmerge(root, sx)}
    >
      <Tab value="none" sx={fallbackTab} />
      {amenityButtonProps.map((buttonProp) => {
        const { type, category } = buttonProp
        return (
          <Tab
            className="FeatureToggleChipTabBar-tab"
            sx={tab}
            icon={
              (getIcon(buttonProp) || (
                <SavedSearchRoundedIcon />
              )) as ReactElement
            }
            value={`${type}::${category}`}
            onClick={() => onClick(type, category)}
            label={t(`${type}.${category}`)}
            key={`${type}-quick-search-${category}`}
          />
        )
      })}
    </ScrollableTab>
  )
}
